import { useContext, useEffect, useMemo, useState } from 'react';
import {
    ActionDependant,
    evaluateTemplateValue,
    evaluateTemplateValueCollection,
    GlobalDataContext,
    TemplateContext,
} from '@ea-lab/reactive-json';
import { Briefcase, Globe, Lock, Users, UsersRound } from 'lucide-react';
import { getSpaceVisibility } from './docVisibility';
import { unwrap } from '../../../utils/decoupled';

const CATEGORIES_URL = '/decoupled-api/taxonomy_term/doc_space_category/collection?display=decoupled';

function normalizeCategory(item) {
    const d = item.data ?? item;
    const rawParent = d.parentId;
    const parentId = Array.isArray(rawParent)
        ? (rawParent[0] ? parseInt(rawParent[0], 10) : 0)
        : (rawParent ? parseInt(rawParent, 10) : 0);
    return { id: d.id, name: d.name, parentId: parentId || 0 };
}

export const DocSpaceHeaderMeta = ({ props }) => {
    const globalDataContext = useContext(GlobalDataContext);
    const templateContext = useContext(TemplateContext);

    const ev = (val) => evaluateTemplateValue({ valueToEvaluate: val, templateContext, globalDataContext });
    const evCol = (val) => evaluateTemplateValueCollection({ valueToEvaluate: val, templateContext, globalDataContext });

    const projectId = ev(props.projectId);
    const rawProjects = evCol(props.projects) || [];
    const rawCategoryIds = ev(props.spaceCategoryIds);
    const rawGrants = ev(props.accessGrants);
    const rawWikiGroupIds = ev(props.wikiGroupIds);
    const rawParentId = ev(props.parentId);
    const rawOwnerId = ev(props.ownerId);
    const rawDirectories = evCol(props.directories) || [];

    const parentId = typeof rawParentId === 'number' ? rawParentId : parseInt(rawParentId || '0', 10);
    const isRoot = parentId === 0 || !parentId;
    const ownerId = rawOwnerId ? parseInt(rawOwnerId, 10) : 0;

    const spaceCategoryIds = useMemo(
        () => (Array.isArray(rawCategoryIds) ? rawCategoryIds.map(Number).filter(Number.isFinite) : []),
        [rawCategoryIds],
    );
    const accessGrants = useMemo(
        () => (Array.isArray(rawGrants) ? rawGrants : []),
        [rawGrants],
    );
    const wikiGroupIds = useMemo(
        () => (Array.isArray(rawWikiGroupIds) ? rawWikiGroupIds.map(Number).filter(Number.isFinite) : []),
        [rawWikiGroupIds],
    );
    const childDirectories = useMemo(
        () => (Array.isArray(rawDirectories) ? rawDirectories : []).map((item) => {
            const d = item.data ?? item;
            return { overrideAccess: d.overrideAccess || false, accessGrants: d.accessGrants || [] };
        }),
        [rawDirectories],
    );

    const localProject = useMemo(() => {
        if (!projectId) return null;
        const pid = parseInt(projectId, 10);
        const found = rawProjects.find((p) => {
            const d = p.data ?? p;
            return parseInt(d.id, 10) === pid;
        });
        if (!found) return null;
        const d = found.data ?? found;
        return { id: d.id, name: d.name || `Projet #${d.id}` };
    }, [projectId, rawProjects]);

    const [fetchedProject, setFetchedProject] = useState(null);

    useEffect(() => {
        if (!projectId || localProject) { setFetchedProject(null); return; }
        let cancelled = false;
        const pid = parseInt(projectId, 10);
        fetch(`/decoupled-api/group/project/${pid}?display=decoupled`, { credentials: 'include' })
            .then((r) => r.ok ? r.json() : null)
            .then((json) => {
                if (cancelled) return;
                setFetchedProject({ id: pid, name: unwrap(json)?.name || `Projet #${pid}` });
            })
            .catch(() => { if (!cancelled) setFetchedProject({ id: pid, name: `Projet #${pid}` }); });
        return () => { cancelled = true; };
    }, [projectId, localProject]);

    const project = localProject || fetchedProject;

    const [categories, setCategories] = useState([]);
    const [loadingCats, setLoadingCats] = useState(false);

    useEffect(() => {
        if (!isRoot || spaceCategoryIds.length === 0) return;
        let cancelled = false;
        setLoadingCats(true);
        fetch(CATEGORIES_URL, { credentials: 'include' })
            .then((r) => { if (!r.ok) throw new Error(`${r.status}`); return r.json(); })
            .then((data) => {
                if (cancelled) return;
                const all = (Array.isArray(data) ? data : [])
                    .filter((item) => item?.data ?? item?.id != null)
                    .map(normalizeCategory);
                setCategories(all.filter((c) => spaceCategoryIds.includes(c.id)));
            })
            .catch(() => { if (!cancelled) setCategories([]); })
            .finally(() => { if (!cancelled) setLoadingCats(false); });
        return () => { cancelled = true; };
    }, [isRoot, spaceCategoryIds]);

    const hasProject = !!project;
    const hasCats = categories.length > 0;
    const hasCatIds = spaceCategoryIds.length > 0;

    const visibility = isRoot
        ? getSpaceVisibility(accessGrants, wikiGroupIds, ownerId, childDirectories)
        : null;
    const sharedAccessCount = accessGrants.filter(g => !(g.grantType === 'user' && String(g.targetId) === String(ownerId))).length;

    if (!isRoot && !hasProject) return null;

    return (
        <ActionDependant {...props}>
            <div id="doc-space-header-meta" className="flex items-center gap-2 flex-wrap mt-1.5">
                {project && (
                    <a
                        href={`/kanban2?id=${project.id}`}
                        className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-2xs font-medium bg-iris-50 text-iris-700 hover:bg-iris-100 transition-colors border border-iris-200"
                    >
                        <Briefcase size={11} />
                        {project.name}
                    </a>
                )}

                {hasProject && (hasCats || hasCatIds || isRoot) && (
                    <span className="w-px h-3.5 bg-stone-200 flex-shrink-0" aria-hidden="true" />
                )}

                {hasCats && categories.map((cat) => (
                    <span
                        key={cat.id}
                        className="inline-flex items-center px-2 py-0.5 rounded-full text-2xs font-medium text-stone-600 bg-stone-100"
                    >
                        {cat.name}
                    </span>
                ))}

                {loadingCats && hasCatIds && !hasCats && (
                    <span className="text-2xs text-stone-300">…</span>
                )}

                {(hasProject || hasCats) && isRoot && (
                    <span className="w-px h-3.5 bg-stone-200 flex-shrink-0" aria-hidden="true" />
                )}

                {isRoot && visibility === 'private' && (
                    <span className="inline-flex items-center gap-1 text-2xs text-stone-400 font-medium" title="Espace privé">
                        <Lock size={11} />
                        Privé
                    </span>
                )}

                {isRoot && visibility === 'shared' && (
                    <span className="inline-flex items-center gap-1 text-2xs text-blue-600 font-medium" title={sharedAccessCount > 0 ? `${sharedAccessCount} accès partagés` : 'Partagé'}>
                        <Users size={12} className="text-blue-500" />
                        Partagé
                    </span>
                )}

                {isRoot && visibility === 'open' && (
                    <span className="inline-flex items-center gap-1 text-2xs text-stone-400 font-medium" title="Aucune restriction d'accès">
                        <Globe size={11} />
                        Ouvert
                    </span>
                )}

                {isRoot && wikiGroupIds.length > 0 && (
                    <>
                        <span className="w-px h-3.5 bg-stone-200 flex-shrink-0" aria-hidden="true" />
                        <span className="inline-flex items-center gap-1 text-2xs text-teal-600 font-medium" title={`${wikiGroupIds.length} groupe${wikiGroupIds.length > 1 ? 's' : ''} wiki`}>
                            <UsersRound size={12} className="text-teal-400" />
                            {wikiGroupIds.length} groupe{wikiGroupIds.length > 1 ? 's' : ''}
                        </span>
                    </>
                )}
            </div>
        </ActionDependant>
    );
};
